'use client';

import { FC, ReactNode, useMemo, useState } from 'react';
import { Dialog, DialogContent } from '../../shared/Dialog';
import {
  ItineraryDialogContent,
  type ItineraryDialogContentProps,
} from '../../website/ItineraryDialogContent/ItineraryDialogContent';
import {
  trackEvent,
  normalizeTrackingValue,
  EventClickElement,
} from '@tuic/tracking';
import { Image, ImageProps } from '../../shared/Image';
import {
  Button,
  IconButton,
  Skeleton,
  SkeletonTextLines,
} from '../../../core-components';
import { convertCtaListToButtonProps, CtaList } from '../../../utils/cta';

export type DashboardTripCardProps = {
  fallback?: boolean;

  image?: ImageProps;
  shipName?: string;
  tripName?: string;
  ports?: string[];
  dateExpression?: string;

  cta?: CtaList;

  dialogTitle?: string;
  itineraryDialogContent?: ItineraryDialogContentProps;
};

const TRACKING_NAME = 'itinerary';

export const DashboardTripCard: FC<DashboardTripCardProps> = ({
  dialogTitle,
  tripName,
  shipName,
  itineraryDialogContent,
  image,
  cta,
  dateExpression,
  fallback,
}) => {
  const [isItineraryDialogOpen, setItineraryDialogOpen] = useState(false);

  const openItineraryDialog = () => {
    setItineraryDialogOpen(true);

    trackEvent({
      event_name: 'layer-open-user',
      layer_name: normalizeTrackingValue(TRACKING_NAME),
    });
  };

  const ctaButtonProps = useMemo(() => convertCtaListToButtonProps(cta), [cta]);

  return (
    <>
      <div className="flex flex-col md:flex-row p-2 gap-2 bg-white text-marine-high-emphasis rounded-sm h-full">
        <div className="aspect-16/9 md:aspect-auto md:w-[50%] rounded-sm overflow-hidden relative">
          <TripImage fallback={fallback} image={image} />
          {!fallback && (
            <IconButton
              iconName="map"
              variant="primary"
              on="image"
              onClick={() => openItineraryDialog()}
              className="absolute left-2 top-2"
            />
          )}
        </div>
        <div className="flex flex-col gap-4 md:w-[50%] p-6">
          <div className="flex flex-col gap-2">
            {fallback ? (
              <Skeleton className="h-4 w-[40%]" />
            ) : (
              shipName && (
                <p className="headline-xs uppercase italic">{shipName}</p>
              )
            )}

            {fallback ? (
              <SkeletonTextLines number={2} className="h-5 w-full" />
            ) : (
              tripName && <p className="headline-md">{tripName}</p>
            )}
          </div>

          {fallback ? (
            <Skeleton className="h-3 w-[80%]" />
          ) : (
            dateExpression && <p className="font-semibold text-lg">{dateExpression}</p>
          )}

          {(fallback || (ctaButtonProps && ctaButtonProps.length > 0)) && (
            <div className="flex flex-col gap-4 pt-8 mt-auto">
              {fallback ? (
                <Button fallback className="w-full" />
              ) : (
                ctaButtonProps?.map((buttonProps, index) => (
                  <Button
                    key={index}
                    {...buttonProps}
                    variant={buttonProps.variant ?? 'primary'}
                  />
                ))
              )}
            </div>
          )}
        </div>
      </div>
      <Dialog
        trackingName={TRACKING_NAME}
        open={isItineraryDialogOpen}
        onOpenChange={setItineraryDialogOpen}
      >
        <DialogContent title={dialogTitle} desktopSize="lg" disableSpacing>
          <div className="bg-ocean-40">
            <ItineraryDialogContent {...itineraryDialogContent} />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const TripImage: FC<{
  image?: ImageProps;
  fallback?: boolean;
  title?: ReactNode;
  href?: string;
}> = ({ fallback, image, title, href }): ReactNode => {
  if (fallback) {
    return <Skeleton className="size-full" />;
  }

  if (!image) {
    return null;
  }

  // If there's a href, make it clickable
  if (href) {
    return (
      <a
        href={href}
        className="absolute inset-0 cursor-pointer"
        data-track-click-element={EventClickElement.Card}
        data-track-click-label={`image.${title}`}
      >
        <Image {...image} className="size-full aspect-auto" />
      </a>
    );
  }

  // Non-clickable image (original behavior)
  return <Image {...image} className="size-full aspect-auto" />;
};
